import { createEffect, sample } from 'effector';
import { createGate } from 'effector-react';
import { cache, createQuery } from '@farfetched/core';
import { formatPhone } from '@tools/formatPhone';
import { getRelationAccountResidents } from './api';
import type {
  GetRelationAccountResidentsPayload,
  GetRelationAccountResidentsResponse,
  Gate,
  ResidentUser,
  ResidentEnterprise,
} from './types';

export const RelationAccountResidentsControlGate = createGate<Gate>();

const fxGetRelationAccountResidents = createEffect<
  GetRelationAccountResidentsPayload,
  GetRelationAccountResidentsResponse,
  Error
>().use(getRelationAccountResidents);

export const relationAccountResidentsQuery = createQuery({
  name: 'relation-account-residents',
  effect: fxGetRelationAccountResidents,
  mapData: ({ result }) => {
    if (!Array.isArray(result?.residents)) {
      return [];
    }

    return result.residents.map((resident) => ({
      id: resident.abstract_account_id,
      label:
        (resident as ResidentEnterprise)?.title || (resident as ResidentUser)?.fio || '',
      phone: resident?.phone ? formatPhone(resident.phone) : '',
      email: resident?.email ?? '',
    }));
  },
});

cache(relationAccountResidentsQuery);

sample({
  clock: RelationAccountResidentsControlGate.state,
  source: {
    status: RelationAccountResidentsControlGate.status,
    state: RelationAccountResidentsControlGate.state,
  },
  filter: ({ status, state: { propertyGuid, accountId } }) =>
    Boolean(status && propertyGuid && accountId),
  fn: ({ state: { propertyGuid, accountId } }) => ({
    area_guid: propertyGuid as string,
    personal_account_id: accountId as string,
  }),
  target: relationAccountResidentsQuery.start,
});
